import basicAppConfig from "@next-platform/client-common-core/infra/constants/basicAppConfig";
import { Button, ButtonSize, Checkbox, Modal, Text, TextVariants } from "@next-platform/client-common-spicy-ui";
import React, { useCallback, useState } from "react";

import { Row } from "@/components/customComponents";
import { SiteLogo } from "@/components/icons";
import { LocalizedLink } from "@/components/LocalizedLink/LocalizedLink";
import Trans from "@/components/Trans/Trans";
import { EXTERNAL_ROUTES_FOR_APPLICATION } from "@/constants";
import useAuth from "@/services/useAuth";

import { GuidelinesModalProps } from "../types";
import { getOnboardingDraft, updateOnboardingDraft } from "../utils";

const getCommunityGuidelinesContent = (): JSX.Element[] => [
  <Trans i18nKey="user:onboarding.guidelines.item1" />,
  <Trans i18nKey="user:onboarding.guidelines.item2" />,
  <Trans i18nKey="user:onboarding.guidelines.item3" />,
];

function GuidelinesModal({ submitForm, isSubmitting }: GuidelinesModalProps) {
  const applicationId = basicAppConfig.applicationId;
  const { user } = useAuth();
  const [isGuidelinesAccepted, setIsGuidelinesAccepted] = useState<boolean>(() => {
    const draft = getOnboardingDraft(user?.email);
    return draft?.guidelines === true;
  });
  const [stateIsGuidelinesErrorVisible, setStateIsGuidelinesErrorVisible] = useState(false);
  const isContinueVisuallyDisabled = !isGuidelinesAccepted;

  const handleGuidelinesCheck = (e: React.SyntheticEvent) => {
    const isCheckboxChecked = (e.target as HTMLInputElement).checked;
    setIsGuidelinesAccepted(isCheckboxChecked);
    if (isCheckboxChecked) setStateIsGuidelinesErrorVisible(false);
    updateOnboardingDraft(user?.email, { guidelines: isCheckboxChecked });
  };

  const handleContinue = useCallback(() => {
    if (isContinueVisuallyDisabled) {
      setStateIsGuidelinesErrorVisible(true);
      return;
    }
    submitForm();
  }, [isContinueVisuallyDisabled, submitForm]);

  return (
    <Modal hideCloseButton open={true} dismissable={true} backdrop="blur">
      <Row className="justify-center">
        <SiteLogo type="icon" />
      </Row>
      <Text variant={TextVariants.HEADING_6_BOLD}>
        <Trans i18nKey="user:onboarding.guidelines.modal.title" />
      </Text>
      <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-12">
        <Trans i18nKey="user:onboarding.guidelines.description" />
      </Text>
      <ul className="*:list-disc pl-4 [&>li]:mb-1 marker:text-xs marker:leading-none">
        {getCommunityGuidelinesContent().map((guideline, index) => (
          <li key={index}>
            <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-12">
              {guideline}
            </Text>
          </li>
        ))}
      </ul>
      <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-gray-12">
        <Trans i18nKey="user:onboarding.guidelines.footer" />
      </Text>
      <div>
        <Checkbox
          label={
            <Trans
              i18nKey="user:onboarding.guidelines.checkbox.label"
              components={{
                guidelinesLink: (
                  <LocalizedLink
                    target="_blank"
                    to={EXTERNAL_ROUTES_FOR_APPLICATION[applicationId as keyof typeof EXTERNAL_ROUTES_FOR_APPLICATION]?.guidelines}
                    className="text-blue-10 text-label-md font-medium"
                  />
                ),
              }}
            />
          }
          checked={isGuidelinesAccepted}
          onChange={handleGuidelinesCheck}
        />
        {!!(stateIsGuidelinesErrorVisible && !isGuidelinesAccepted) && (
          <Text variant={TextVariants.PARAGRAPH_SMALL_REGULAR} className="text-red-9 mt-1">
            <Trans i18nKey="user:onboarding.guidelines.error.required" />
          </Text>
        )}
      </div>
      <Button
        size={ButtonSize.LARGE}
        onClick={handleContinue}
        loading={isSubmitting}
        aria-disabled={isContinueVisuallyDisabled}
        className={isContinueVisuallyDisabled ? "opacity-50 cursor-not-allowed" : ""}
      >
        <Trans i18nKey="user:onboarding.guidelines.cta.startChatting" />
      </Button>
    </Modal>
  );
}

export default GuidelinesModal;
